import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { queryClient } from '../api/queryClient';
import type { TimelineBlock } from '../types';
import { timeToMinutes } from '../lib/timeline';
import { requestNotificationPermission } from '../services/timelineBlockChecker';

export function useTimelineBlocks(date: string) {
  const { data } = useQuery<TimelineBlock[]>({
    queryKey: ['timeline-blocks', date],
    queryFn: () => api.timelineBlocks.list(date),
  });
  return (data ?? []) as TimelineBlock[];
}

// One shared query for all upcoming blocks — all subtask chips filter from this cache.
export function useAllUpcomingBlocks(): TimelineBlock[] {
  const { data } = useQuery<TimelineBlock[]>({
    queryKey: ['timeline-blocks', 'upcoming'],
    queryFn: () => api.timelineBlocks.upcoming(),
    staleTime: 30_000,
  });
  return (data ?? []) as TimelineBlock[];
}

export function useNearestFutureBlock(taskId: string): TimelineBlock | undefined {
  const blocks = useAllUpcomingBlocks();
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  return blocks.find((b) =>
    b.taskId === taskId &&
    (b.date > today || (b.date === today && timeToMinutes(b.scheduledStartTime) > currentMinutes))
  );
}

export async function createTimelineBlock(
  taskId: string,
  date: string,
  scheduledStartTime: string,
  scheduledDuration?: number,
  taskMeta?: { title?: string; priority?: string; trackProgress?: number; itemType?: TimelineBlock['itemType']; parentId?: string | null }
): Promise<TimelineBlock> {
  const duration = scheduledDuration ?? 30;
  const tempId = `temp-${Date.now()}`;
  const optimistic = {
    id: tempId, taskId, date, scheduledStartTime, scheduledDuration: duration,
    title: taskMeta?.title ?? '', priority: taskMeta?.priority ?? 'p2', trackProgress: taskMeta?.trackProgress ?? 0,
    itemType: taskMeta?.itemType,
    parentId: taskMeta?.parentId,
  } as TimelineBlock;

  await queryClient.cancelQueries({ queryKey: ['timeline-blocks', date] });
  queryClient.setQueryData<TimelineBlock[]>(
    ['timeline-blocks', date],
    (old = []) => [...old, optimistic]
  );

  try {
    const block = await api.timelineBlocks.create({ taskId, date, scheduledStartTime, scheduledDuration: duration });
    requestNotificationPermission();
    return block as TimelineBlock;
  } finally {
    queryClient.invalidateQueries({ queryKey: ['timeline-blocks', date] });
    queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
  }
}

export async function updateTimelineBlock(
  blockId: string,
  date: string,
  patch: { scheduledStartTime?: string; scheduledDuration?: number; date?: string }
): Promise<TimelineBlock> {
  const targetDate = patch.date ?? date;
  const isCrossDay = patch.date && patch.date !== date;

  // Optimistically update caches
  await queryClient.cancelQueries({ queryKey: ['timeline-blocks', date] });
  await queryClient.cancelQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
  if (isCrossDay) {
    await queryClient.cancelQueries({ queryKey: ['timeline-blocks', targetDate] });
    // Remove from old date, add to new date
    queryClient.setQueryData<TimelineBlock[]>(['timeline-blocks', date], (old) =>
      old?.filter((b) => b.id !== blockId)
    );
    queryClient.setQueryData<TimelineBlock[]>(['timeline-blocks', targetDate], (old) => {
      const existing = queryClient.getQueryData<TimelineBlock[]>(['timeline-blocks', date]);
      const block = existing?.find((b) => b.id === blockId) ??
        (old ?? []).find((b) => b.id === blockId);
      if (!block) return old;
      return [...(old ?? []), { ...block, ...patch, date: targetDate }];
    });
  } else {
    const applyPatch = (old: TimelineBlock[] | undefined) =>
      old?.map((b) => b.id === blockId ? { ...b, ...patch } : b);
    queryClient.setQueryData<TimelineBlock[]>(['timeline-blocks', date], applyPatch);
  }
  queryClient.setQueryData<TimelineBlock[]>(['timeline-blocks', 'upcoming'], (old) =>
    old?.map((b) => b.id === blockId ? { ...b, ...patch, date: targetDate } : b)
  );
  // Also invalidate range query for planner
  await queryClient.cancelQueries({ queryKey: ['timeline-blocks', 'range'] });

  try {
    const block = await api.timelineBlocks.update(blockId, patch);
    return block as TimelineBlock;
  } finally {
    queryClient.invalidateQueries({ queryKey: ['timeline-blocks', date] });
    queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'range'] });
    if (isCrossDay) {
      queryClient.invalidateQueries({ queryKey: ['timeline-blocks', targetDate] });
    }
  }
}

export async function deleteTimelineBlock(blockId: string, date: string): Promise<void> {
  await api.timelineBlocks.delete(blockId);
  queryClient.invalidateQueries({ queryKey: ['timeline-blocks', date] });
  queryClient.invalidateQueries({ queryKey: ['timeline-blocks', 'upcoming'] });
}
